import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
  Stack,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  AccountBalance,
  MonetizationOn,
  Receipt,
  Analytics,
  Download,
  Refresh,
  Close,
  ContentCopy
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { BrowserProvider, Contract, formatEther } from 'ethers';

// Import contract ABIs
import marketplaceContract from '../abis/CarbonCreditMarketplace.json';
import vaultContract from '../abis/PlatformVault.json';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  Filler
);

const MARKETPLACE_ADDRESS = marketplaceContract.networks[process.env.REACT_APP_NETWORK_ID]?.address;
const VAULT_ADDRESS = vaultContract.networks[process.env.REACT_APP_NETWORK_ID]?.address;

const AdminFinancialDashboard = ({
  account,
  isAdmin,
  provider,
  signer,
  vault,
  nft,
  carbon,
  marketplace,
  vaultAddress,
  nftAddress,
  carbonAddress,
  marketplaceAddress,
  history,
  retiredEvents,
  enqueueSnackbar,
  refreshVaultStatus
} = {}) => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [financialData, setFinancialData] = useState({
    totalRevenue: '0',
    totalFees: '0',
    totalVolume: '0',
    activeListings: 0,
    completedSales: 0,
    pendingWithdrawals: '0',
    platformBalance: '0',
    revenueGrowth: 0,
    volumeGrowth: 0
  });
  
  const [chartData, setChartData] = useState({
    revenueChart: { labels: [], datasets: [] },
    volumeChart: { labels: [], datasets: [] },
    feeDistribution: { labels: [], datasets: [] },
    topUsers: []
  });

  const [recentTransactions, setRecentTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);

  // Fetch financial data from blockchain
  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      // Try to use passed props first, fallback to creating new connection
      let currentProvider = provider;
      let currentMarketplace = marketplace;
      let currentVault = vault;

      if (!currentProvider || !currentMarketplace) {
        if (!window.ethereum) {
          throw new Error('No wallet connected');
        }
        
        currentProvider = new BrowserProvider(window.ethereum);
        currentMarketplace = new Contract(
          marketplaceAddress || MARKETPLACE_ADDRESS, 
          marketplaceContract.abi, 
          currentProvider
        );
        currentVault = vaultAddress ? new Contract(vaultAddress || VAULT_ADDRESS, vaultContract.abi, currentProvider) : null;
      }

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 365;
      startDate.setDate(endDate.getDate() - days);

      // Get all marketplace events
      const [
        purchaseEvents,
        listingEvents,
        auctionEvents,
        bidEvents
      ] = await Promise.all([
        currentMarketplace.queryFilter(currentMarketplace.filters.TokenPurchased(null)),
        currentMarketplace.queryFilter(currentMarketplace.filters.TokenListed(null)),
        currentMarketplace.queryFilter(currentMarketplace.filters.AuctionEnded(null)),
        currentMarketplace.queryFilter(currentMarketplace.filters.NewHighestBid(null))
      ]);

      console.log('Fetched events:', {
        purchases: purchaseEvents.length,
        listings: listingEvents.length,
        auctions: auctionEvents.length,
        bids: bidEvents.length
      });

      // Process purchase events for revenue calculation
      let totalVolume = 0;
      let totalFees = 0;
      let completedSales = 0;
      const dailyData = {};
      const userVolumeMap = {};

      for (const event of purchaseEvents) {
        const block = await currentProvider.getBlock(event.blockNumber);
        const eventDate = new Date(block.timestamp * 1000);
        
        if (eventDate >= startDate && eventDate <= endDate) {
          const price = parseFloat(formatEther(event.args.price || '0'));
          const fee = price * 0.025; // 2.5% platform fee
          
          totalVolume += price;
          totalFees += fee;
          completedSales++;

          // Daily aggregation
          const dayKey = eventDate.toISOString().split('T')[0];
          if (!dailyData[dayKey]) {
            dailyData[dayKey] = { volume: 0, fees: 0, sales: 0 };
          }
          dailyData[dayKey].volume += price;
          dailyData[dayKey].fees += fee;
          dailyData[dayKey].sales += 1;

          // User volume tracking
          const buyer = event.args.buyer;
          userVolumeMap[buyer] = (userVolumeMap[buyer] || 0) + price;
        }
      }

      // Get active listings count
      const activeListings = listingEvents.filter(event => {
        // This is a simplified check - in production, you'd need to verify if listing is still active
        return true; // Placeholder
      }).length;

      // Get platform balance if vault exists
      let platformBalance = '0';
      if (currentVault && (vaultAddress || VAULT_ADDRESS)) {
        try {
          const balance = await currentProvider.getBalance(vaultAddress || VAULT_ADDRESS);
          platformBalance = formatEther(balance);
        } catch (error) {
          console.warn('Could not fetch vault balance:', error);
        }
      }

      // Prepare chart data
      const sortedDays = Object.keys(dailyData).sort();
      const revenueChartData = {
        labels: sortedDays.map(day => new Date(day).toLocaleDateString()),
        datasets: [
          {
            label: 'Daily Revenue (ETH)',
            data: sortedDays.map(day => dailyData[day].fees),
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.1,
            fill: true
          }
        ]
      };

      const volumeChartData = {
        labels: sortedDays.map(day => new Date(day).toLocaleDateString()),
        datasets: [
          {
            label: 'Daily Volume (ETH)',
            data: sortedDays.map(day => dailyData[day].volume),
            backgroundColor: 'rgba(54, 162, 235, 0.8)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          }
        ]
      };

      // Fee distribution chart
      const feeDistributionData = {
        labels: ['Platform Fees', 'Creator Royalties', 'Transaction Costs'],
        datasets: [
          {
            data: [totalFees * 0.7, totalFees * 0.25, totalFees * 0.05], // Example distribution
            backgroundColor: [
              'rgba(255, 99, 132, 0.8)',
              'rgba(54, 162, 235, 0.8)',
              'rgba(255, 205, 86, 0.8)'
            ],
            borderWidth: 2
          }
        ]
      };

      // Top users by volume
      const topUsers = Object.entries(userVolumeMap)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([address, volume]) => ({
          address: `${address.slice(0, 6)}...${address.slice(-4)}`,
          fullAddress: address,
          volume: volume.toFixed(4)
        }));

      // Recent transactions (last 10 purchases) with enhanced details
      const recentTxs = await Promise.all(
        purchaseEvents
          .slice(-10)
          .reverse()
          .map(async (event) => {
            try {
              const block = await currentProvider.getBlock(event.blockNumber);
              const txReceipt = await currentProvider.getTransactionReceipt(event.transactionHash);
              const tx = await currentProvider.getTransaction(event.transactionHash);
              
              // Find corresponding listing event to get seller info
              const tokenId = event.args.tokenId?.toString();
              const correspondingListing = listingEvents.find(listingEvent => 
                listingEvent.args.tokenId?.toString() === tokenId
              );
              const seller = correspondingListing?.args.seller || 'Unknown';
              
              return {
                txHash: event.transactionHash,
                tokenId: tokenId || 'N/A',
                price: formatEther(event.args.price || '0'),
                buyer: event.args.buyer,
                seller: seller,
                timestamp: block.timestamp * 1000,
                blockNumber: event.blockNumber,
                gasUsed: txReceipt.gasUsed.toString(),
                gasPrice: tx.gasPrice.toString(),
                transactionFee: formatEther((txReceipt.gasUsed * tx.gasPrice).toString()),
                status: txReceipt.status === 1 ? 'Success' : 'Failed',
                confirmations: await currentProvider.getBlockNumber() - event.blockNumber
              };
            } catch (error) {
              console.warn('Error fetching transaction details:', error);
              return {
                txHash: event.transactionHash,
                tokenId: event.args.tokenId?.toString() || 'N/A',
                price: formatEther(event.args.price || '0'),
                buyer: event.args.buyer,
                seller: 'Unknown',
                timestamp: Date.now(),
                blockNumber: event.blockNumber,
                gasUsed: 'N/A',
                gasPrice: 'N/A',
                transactionFee: 'N/A',
                status: 'Unknown',
                confirmations: 'N/A'
              };
            }
          })
      );

      setFinancialData({
        totalRevenue: totalFees.toFixed(4),
        totalFees: totalFees.toFixed(4),
        totalVolume: totalVolume.toFixed(4),
        activeListings,
        completedSales,
        pendingWithdrawals: '0', // Placeholder
        platformBalance: parseFloat(platformBalance).toFixed(4),
        revenueGrowth: 12.5, // Placeholder
        volumeGrowth: 8.3 // Placeholder
      });

      setChartData({
        revenueChart: revenueChartData,
        volumeChart: volumeChartData,
        feeDistribution: feeDistributionData,
        topUsers
      });

      setRecentTransactions(recentTxs);

    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialData();
  }, [timeRange]);

  const handleExportData = () => {
    // Implementation for exporting financial data
    const data = {
      summary: financialData,
      transactions: recentTransactions,
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `financial-report-${timeRange}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setTransactionDialogOpen(true);
  };

  const handleCloseTransactionDialog = () => {
    setTransactionDialogOpen(false);
    setSelectedTransaction(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      if (enqueueSnackbar) {
        enqueueSnackbar('Copied to clipboard!', { variant: 'success' });
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Financial Dashboard
        </Typography>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading financial data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Financial Dashboard
        </Typography>
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
              <MenuItem value="1y">Last year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchFinancialData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExportData}
          >
            Export
          </Button>
        </Stack>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Revenue
                  </Typography>
                  <Typography variant="h5">
                    {financialData.totalRevenue} ETH
                  </Typography>
                  <Chip
                    label={`+${financialData.revenueGrowth}%`}
                    color="success"
                    size="small"
                    icon={<TrendingUp />}
                  />
                </Box>
                <MonetizationOn color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Trading Volume
                  </Typography>
                  <Typography variant="h5">
                    {financialData.totalVolume} ETH
                  </Typography>
                  <Chip
                    label={`+${financialData.volumeGrowth}%`}
                    color="success"
                    size="small"
                    icon={<TrendingUp />}
                  />
                </Box>
                <Analytics color="secondary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Platform Balance
                  </Typography>
                  <Typography variant="h5">
                    {financialData.platformBalance} ETH
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Available for withdrawal
                  </Typography>
                </Box>
                <AccountBalance color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Completed Sales
                  </Typography>
                  <Typography variant="h5">
                    {financialData.completedSales}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {financialData.activeListings} active listings
                  </Typography>
                </Box>
                <Receipt color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue Trend
              </Typography>
              <Box sx={{ height: 300 }}>
                <Line
                  data={chartData.revenueChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Revenue (ETH)'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fee Distribution
              </Typography>
              <Box sx={{ height: 300 }}>
                <Doughnut
                  data={chartData.feeDistribution}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Volume Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Trading Volume
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar
                  data={chartData.volumeChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Volume (ETH)'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Users */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Traders
              </Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {chartData.topUsers.map((user, index) => (
                  <Box
                    key={user.fullAddress}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: index < chartData.topUsers.length - 1 ? 1 : 0,
                      borderColor: 'divider'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        #{index + 1}
                      </Typography>
                      <Tooltip title={user.fullAddress}>
                        <Typography variant="body2" color="textSecondary">
                          {user.address}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {user.volume} ETH
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Transactions
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Token ID</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Buyer</TableCell>
                  <TableCell>Transaction</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentTransactions.map((tx, index) => (
                  <TableRow key={index}>
                    <TableCell>#{tx.tokenId}</TableCell>
                    <TableCell>{parseFloat(tx.price).toFixed(4)} ETH</TableCell>
                    <TableCell>
                      <Tooltip title={tx.buyer}>
                        <span>
                          {tx.buyer.slice(0, 6)}...{tx.buyer.slice(-4)}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewTransaction(tx)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                    <TableCell>
                      {new Date(tx.timestamp).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Transaction Details Dialog */}
      <Dialog 
        open={transactionDialogOpen} 
        onClose={handleCloseTransactionDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Transaction Details
            <IconButton onClick={handleCloseTransactionDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedTransaction && (
            <Box sx={{ py: 2 }}>
              {/* Transaction Hash */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Transaction Hash
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {selectedTransaction.txHash}
                  </Typography>
                  <IconButton size="small" onClick={() => copyToClipboard(selectedTransaction.txHash)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Transaction Details Grid */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Token ID
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    #{selectedTransaction.tokenId}
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Price
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {parseFloat(selectedTransaction.price).toFixed(4)} ETH
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip 
                    label={selectedTransaction.status}
                    color={selectedTransaction.status === 'Success' ? 'success' : 'error'}
                    size="small"
                    sx={{ mb: 2 }}
                  />

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Block Number
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedTransaction.blockNumber}
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Confirmations
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransaction.confirmations}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Buyer
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {selectedTransaction.buyer}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(selectedTransaction.buyer)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Seller
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {selectedTransaction.seller}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(selectedTransaction.seller)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Gas Used
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedTransaction.gasUsed}
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Transaction Fee
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedTransaction.transactionFee} ETH
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Timestamp
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedTransaction.timestamp).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseTransactionDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminFinancialDashboard;
